import { DatePipe, DecimalPipe } from '@angular/common';
import { NFIdentity, PivotAggregates, ValueType } from '@nibras/core/common';
import * as FileSaver from 'file-saver';
import moment from 'moment';
import { LocaleService } from './locale.service';
import { RoleAccess, UserRolePermission } from './model/access-security.model';
import { App, DataComponentConfig } from './model/app.model';
import { ColDefExt } from './model/column-config.model';
import { AppConstants } from './model/constants.model';
import { dcTagFlatTree } from './model/dc-tag-flat-tree.model';
import { dcAttributeDataKey } from './model/dcAttributeDataKey.model';
import { dcTag } from './model/dcTag.model';
import { dcValue } from './model/dcvalue.model';
import {
  addNumbers,
  maxValue,
  minValue
} from './model/formula/formula-supported-methods/arithmatic-functions';
import { GenericValue } from './model/generic-value.model';
import { RoleAssignment, User } from './model/user.model';

export class SharedCommonService {
  public static roundOff(value: number, precision: number): number {
    if (Number.isNaN(value)) return 0;
    //TODO: TEMP Code - Below undefined condition should be removed once we have proper precision handling in place.
    //BA needs to review all the Attributes where precision is used.
    if (precision === undefined) {
      precision = 0;
    } else if (precision == null) {
      return value;
    }

    if (value != Math.floor(value)) {
      let values;
      if (this.isExponentialValue(value)) {
        values = Number.parseFloat(value as any).toPrecision(4);
      } else {
        values = Number.parseFloat(value as any).toFixed(precision);
      }
      return parseFloat(values);
    } else {
      return value;
    }
  }

  public static isExponentialValue(value: number): boolean {
    if (value == null) {
      return false;
    } else {
      return value.toString().includes('e');
    }
  }

  public static roundOffPrecision(valuePrecision: number): number {
    return this.roundOff(valuePrecision, AppConstants.DecimalPrecison);
  }

  public static precisionCount(value: number) {
    if (Number.isNaN(value)) return 0;
    if (Math.floor(value) === value) return 0;
    return (value && value.toString().split('.')[1].length) || 0;
  }

  public static formatNumber(inputNumber: number): string {
    const decimalPipe = new DecimalPipe(LocaleService.locale);
    const formatedString = decimalPipe.transform(inputNumber);
    return formatedString;
  }

  public static setEndDate(endDate: Date, roundEndTimeToCurrentTime = true) {
    if (!endDate) {
      return null;
    }
    const endDateTime = new Date(endDate);
    const currentDate = new Date();
    if (roundEndTimeToCurrentTime && endDateTime.toDateString() === currentDate.toDateString()) {
      endDateTime.setHours(
        currentDate.getHours(),
        currentDate.getMinutes(),
        currentDate.getSeconds()
      );
    }
    return endDateTime;
  }

  public static formatDate(inputDate: string | number | Date, onlyDate?: boolean): string {
    if (onlyDate) {
      inputDate = this.convertToDateWithOutTime(inputDate as string);
    }

    if (
      typeof inputDate == 'string' &&
      inputDate.length !== 10 &&
      !(inputDate as string).includes('T00:00:00')
    ) {
      //This is a temp fix, we might need a new ValueType called DateWithTime or just "Date" to handle these.
      return this.formatDateTime(inputDate);
    } else {
      return this._formatDate(inputDate);
    }
  }

  public static getAppDateFormat() {
    if (LocaleService.userSettings && LocaleService.userSettings.DateTimeFormat) {
      return LocaleService.userSettings.DateFormat;
    } else {
      return AppConstants.DATE_FORMAT;
    }
  }

  public static formatRichTextValue(data: string) {
    const hiddenCharacterRegex = /[\x00-\x1F\x7F-\x9F\uFEFF]/g;
    const formattedRichTextValue: string = data?.replace(hiddenCharacterRegex, '');
    return formattedRichTextValue;
  }

  public static setAnchorTarget(commentContent: string) {
    const doc = new DOMParser().parseFromString(commentContent, 'text/html');
    const links = doc.querySelectorAll('a');
    if (!links?.length) {
      return doc.body.innerHTML;
    }
    links.forEach(link => {
      if (!link.getAttribute('target')) {
        link.setAttribute('target', '_blank');
      }
    });

    return doc.body.innerHTML;
  }

  private static _formatDate(inputDate: string | number | Date): string {
    if (LocaleService.userSettings && LocaleService.userSettings.DateFormat) {
      const datePipe = new DatePipe('en-US');
      const formatedDate = datePipe.transform(inputDate, LocaleService.userSettings.DateFormat);
      return formatedDate;
    } else {
      return new Date(inputDate).toLocaleDateString(
        LocaleService.locale,
        AppConstants.DATE_FORMAT_OPTIONS
      );
    }
  }

  public static formatDateTime(inputDate: string | number | Date, hideSeconds = false): string {
    if (LocaleService.userSettings && LocaleService.userSettings.DateTimeFormat) {
      let format = LocaleService.userSettings.DateTimeFormat;
      if (hideSeconds) {
        format = format.replace(':ss', '');
      }
      const datePipe = new DatePipe('en-US');
      const formatedDate = datePipe.transform(inputDate, format);
      return formatedDate;
    } else {
      return new Date(inputDate).toLocaleTimeString(
        LocaleService.locale,
        AppConstants.DATE_FORMAT_OPTIONS
      );
    }
  }

  public static formatDateByMoment(date: Date, format: string) {
    try {
      if (date && format) {
        return moment(date).format(format);
      } else {
        return moment(date).format('DD-MMM-YYYY');
      }
    } catch (error) {
      console.log(error);
      return null;
    }
  }

  public static getDateTimeFromNow(date: Date, withoutSufix = true) {
    return moment(date).fromNow(withoutSufix);
  }

  /**
   *
   * This function returns the difference between times like 2M 4d 5m (for 2 Months 4 Days 5 Mins)
   *
   * @param start_time - start_time of an event
   * @param end_time - end_time of an event
   */
  static getFormattedDuration(start_time: Date, end_time: Date): string {
    const dateEnd = +end_time;
    const dateStart = +start_time;

    let seconds: any;
    if (dateEnd > dateStart) {
      seconds = Math.floor((dateEnd - dateStart) / 1000);
    } else {
      seconds = Math.floor((dateStart - dateEnd) / 1000);
    }

    let minutes = Math.floor(seconds / 60);
    let hours = Math.floor(minutes / 60);

    let days = Math.floor(hours / 24);
    let months = Math.floor(days / 30);
    const years = Math.floor(months / 12);

    months = months - years * 12;
    days = days - months * 30;
    hours = hours - days * 24;
    minutes = minutes - days * 24 * 60 - hours * 60;
    seconds = seconds - days * 24 * 60 * 60 - hours * 60 * 60 - minutes * 60;

    let finalStr = '';
    if (years) {
      finalStr = finalStr + years + ' Y';
    }
    if (months) {
      finalStr = finalStr + '  ' + months + ' M';
    }
    if (days) {
      finalStr = finalStr + '  ' + days + ' d';
    }
    if (hours) {
      finalStr = finalStr + '  ' + hours + ' h';
    }
    if (minutes) {
      finalStr = finalStr + '  ' + minutes + ' m';
    }
    if (seconds) {
      finalStr = finalStr + '  ' + seconds + ' s';
    }
    return finalStr;
  }

  // public static parseDateForApi(date: Date) {
  //   return moment(date).format(AppConstants.ISO_DATE_FORMAT) as string;
  // }

  public static parseDateForApi(date: Date | string) {
    //CHANGE HISTORY
    //OLD - This method is to Fix the -1 Date issue. If you test afternoon selected date is send minus one date from browser to API.
    //22-Jul-2020 - Changing toLocaleString to toISOString.
    //This will help to connect from any client with any dateformat as ISO is a standard date format.
    //All Date inputs to API are supposed to be in ISO format.
    //TODO: Timezone is not yet condidered, need to take care of this as well.
    //Now the assumption is that Server API, DB, Client will be in Same timezone.
    if (date instanceof Date) {
      return this.getLocalISODateString(date) as any;
    }
    if (date && (date as any).toDate) {
      //check if moment type.
      return this.getLocalISODateString((date as any).toDate()) as any;
    }
    return date;
  }

  public static isDateFormatValid(event: moment.Moment, withTime = false) {
    if (!event) {
      return false;
    }

    const rawInput = event.creationData ? event.creationData() : null;
    let input: moment.Moment | string;
    if (rawInput && rawInput.input && typeof rawInput.input === 'string') {
      input = rawInput.input.trim();
    } else {
      input = event;
    }

    const momentDate: moment.Moment = moment(
      input,
      SharedCommonService.getMomentDateFormat(withTime),
      true
    );

    return momentDate.isValid();
  }

  public static getMomentDateFormat(withTime: boolean): string {
    return (
      (LocaleService.userSettings.DateFormat || AppConstants.DATE_FORMAT).toUpperCase() +
      (withTime
        ? ' ' +
        (LocaleService.userSettings.DateTimeFormat.replace(
          LocaleService.userSettings.DateFormat,
          ''
        ).trim() || AppConstants.DATE_FORMAT)
        : '')
    ).trim();
  }

  public static getLocalISODateString(date: Date) {
    const tzo = -date.getTimezoneOffset();
    const dif = tzo >= 0 ? '+' : '-';
    const pad = (num: number) => {
      const norm = Math.floor(Math.abs(num));
      return (norm < 10 ? '0' : '') + norm;
    };
    return (
      date.getFullYear() +
      '-' +
      pad(date.getMonth() + 1) +
      '-' +
      pad(date.getDate()) +
      'T' +
      pad(date.getHours()) +
      ':' +
      pad(date.getMinutes()) +
      ':' +
      pad(date.getSeconds()) +
      '.' +
      date.getMilliseconds() +
      dif +
      pad(tzo / 60) +
      ':' +
      pad(tzo % 60)
    );
  }

  public static getDateNowISOString() {
    return this.parseDateForApi(new Date());
  }

  public static toDateTime(dateString: string | Date) {
    if (dateString && typeof dateString == 'string') return new Date(dateString);
    return dateString as Date;
  }

  public static convertToDateWithOutTime(dateString: string | Date) {
    const date = this.toDateTime(dateString);
    this.clearTime(date);
    return date;
  }

  public static clearTime(date: Date) {
    if (!date) {
      return;
    }
    date.setHours(0);
    date.setMinutes(0);
    date.setSeconds(0);
    date.setMilliseconds(0);
  }

  public static getValueFromDcValue(attributeValue: dcValue, valueTypeID: ValueType): GenericValue {
    switch (valueTypeID) {
      case ValueType.Int16:
        return attributeValue.ValueInt;
      case ValueType.Int32:
        return attributeValue.ValueInt;
      case ValueType.Int64:
        return attributeValue.ValueInt;
      case ValueType.String:
        return attributeValue.ValueString;
      case ValueType.Double:
        return attributeValue.ValueDouble;
      case ValueType.DateTime:
        return attributeValue.ValueTime;
      case ValueType.Boolean:
        return attributeValue.ValueBoolean;
      default:
        return attributeValue.ValueString || attributeValue.Value;
    }
  }

  public static getSnapshotValue(
    selectedDataValues: dcValue[],
    valueTypeID: ValueType
  ): GenericValue {
    const selectedValue = selectedDataValues[0];
    // let selectedValue: any;
    // if (selectedDataValues) {
    //   selectedValue = selectedDataValues;
    // } else {
    //   selectedValue = selectedDataValues[0];
    // }
    if (!selectedValue) return '';
    if (selectedValue.IsGood) {
      let value = this.getValueFromDcValue(selectedValue, valueTypeID);
      if (!value) value = selectedValue.Value;
      return value != null ? value : selectedValue.ValueDouble;
    }
    return selectedValue.MessageString ? selectedValue.MessageString : '';
  }

  public static tableCellDateFormater(value: string, onlyDate = false) {
    if (value && !isNaN(Date.parse(value))) {
      if (isNaN(value as any)) {
        return SharedCommonService.formatDate(value, onlyDate);
      } else return value;
    } else return value;
  }

  public static getConditionFormattingColumnName(column: string) {
    return AppConstants.CONDITION_FORMATTING_COLUMNS_PREFIX + column;
  }

  public static hasValidDCValue(data: dcValue) {
    return data && this.hasValidData(data.Value);
  }

  public static getValidDataValue(value: string | number | boolean) {
    return this.hasValidData(value) ? value : 0;
  }

  public static hasValidData(data: GenericValue) {
    return (
      data !== undefined &&
      data !== null &&
      data !== '' &&
      (typeof data != 'string' ||
        (typeof data == 'string' && data.toLowerCase() != AppConstants.NO_DATA_TEXT))
    );
  }

  public static hasValidNumber(data: number) {
    return this.hasValidData(data) && !isNaN(data);
  }

  public static hasValidNonEmptyData(data: string | number | boolean) {
    return (
      this.hasValidData(data) &&
      (typeof data != 'string' || (typeof data == 'string' && data !== ''))
    );
  }

  public static isAnalysisKey(dataKey: dcAttributeDataKey) {
    return dataKey.ElementName == AppConstants.AttributeNames.ANALYSIS;
  }

  public static isDataKeyFormulaDR(dataKey: dcAttributeDataKey) {
    return dataKey.QueryManagerID && dataKey.QueryManagerID == NFIdentity.FormulaDR ? true : false;
  }

  public static getColumName(name: string) {
    if (!name) return '';
    return name.trim().replace(/[\.]/g, '_');
  }

  public static filterDataKeys(dataKeys: dcAttributeDataKey[], dataKey: dcAttributeDataKey) {
    const filteredDataKeys = dataKeys.filter(
      x =>
        x.RootElementVersionID == dataKey.RootElementVersionID &&
        x.ChildElementPath == dataKey.ChildElementPath
    );
    return filteredDataKeys;
  }

  public static filterDataKeysByDataComponent(
    dataKeys: dcAttributeDataKey[],
    dataComponentIds: DataComponentConfig[]
  ) {
    const filteredDataKeys = dataKeys.filter(keys =>
      dataComponentIds?.some(dataComponentId =>
        SharedCommonService.matchByDataComponent(keys, dataComponentId)
      )
    );
    return filteredDataKeys;
  }

  public static filterDataKeysByRootElementVersionID(
    dataKeys: dcAttributeDataKey[],
    dataComponentIds: DataComponentConfig[]
  ) {
    //let storageHolder = '';
    dataKeys.forEach((dataKey, index) => {
      //storageHolder = dataKey.AttributeDataKey[0];
      dataComponentIds[index].RootElementVersionID = +dataKey.AttributeDataKey[0]; //+storageHolder;
    });
    const filteredDataKeys = dataKeys.filter(keys =>
      dataComponentIds.some(dataComponentId =>
        SharedCommonService.matchByDataComponent(keys, dataComponentId)
      )
    );
    return filteredDataKeys;
  }

  public static findDataKeyByComponentConfig(
    dataKeys: dcAttributeDataKey[],
    dataComponentConfig: DataComponentConfig,
    processInstanceID = 0
  ) {
    const filteredDataKeys = dataKeys.find(keys =>
      SharedCommonService.matchByDataComponent(keys, dataComponentConfig, processInstanceID)
    );
    return filteredDataKeys;
  }

  public static matchByDataComponent(
    key: dcAttributeDataKey,
    dataComponentConfig: DataComponentConfig,
    processInstanceID = 0
  ) {
    if (dataComponentConfig.ChildElementPath && dataComponentConfig.RootElementVersionID) {
      return (
        dataComponentConfig.Id === key.DataComponentID &&
        (dataComponentConfig.ChildElementPath
          ? dataComponentConfig.ChildElementPath == key.ChildElementPath
          : key.ChildElementPath === AppConstants.CHILD_ELEMENT_DEFAULT_PATH) &&
        (dataComponentConfig.RootElementVersionID
          ? dataComponentConfig.RootElementVersionID === key.RootElementVersionID
          : !dataComponentConfig.RootElementVersionID)
      );
    } else if (processInstanceID) {
      return (
        dataComponentConfig.Id === key.DataComponentID && key.ProcessInstanceID == processInstanceID
      );
    } else {
      return dataComponentConfig.Id === key.DataComponentID;
    }
  }

  public static buildComponentIdByComponent(
    componetIdPrefix: string,
    dataComponentConfig: DataComponentConfig,
    componentId?: number,
    readOnly = false
  ) {
    return this.buildComponentID(
      componetIdPrefix,
      dataComponentConfig || ({ Id: componentId } as DataComponentConfig),
      readOnly
    );
  }

  public static buildComponentID(
    componetIdPrefix: string,
    dataComponentConfig: DataComponentConfig,
    readOnly = false,
    componentId?: number
  ) {
    const prefix = `${readOnly ? '#' : ''}${AppConstants.COMPONENT_TEXT}`;
    const nameId = dataComponentConfig
      ? `${componetIdPrefix}_${dataComponentConfig.Id}${dataComponentConfig.ChildElementPath &&
      dataComponentConfig.ChildElementPath.replace(/[\.\\]/g, '_')
      }${dataComponentConfig.RootElementVersionID && dataComponentConfig.RootElementVersionID}`
      : componentId
        ? `${componentId}`
        : AppConstants.COMPONENT_TEXT;

    return `${prefix}${nameId}`;
  }

  public static buildWidgetDynamicComponentID(dataComponentConfig: string, readOnly = false) {
    return dataComponentConfig
      ? `${readOnly ? '#' : ''}${AppConstants.COMPONENT_TEXT}${dataComponentConfig}`
      : AppConstants.COMPONENT_TEXT;
  }

  /**
   * Get the difference between two date using moment. https://momentjs.com/docs/#/displaying/difference/
   *
   * @param startDate : Pass date string or actual date object
   * @param endDate : Pass  date string or actual date object
   * @param measurement : years, months, weeks, days, hours, minutes, and seconds
   */
  public static dateDifference(startDate: any, endDate: any, measurement: any = 'days') {
    const a = moment(endDate);
    const b = moment(startDate);
    if (measurement == null) {
      return a.diff(b);
    } else {
      return a.diff(b, measurement);
    }
  }

  public static dateTimeDifference(endDate: any) {
    let lastDate = moment();
    const todayDate = moment();
    const daysDifference = SharedCommonService.dateDifference(todayDate, endDate);
    if (daysDifference > 0) {
      lastDate = moment(endDate);
      lastDate.set({ hour: 23, minute: 59, second: 59 });
    }
    return moment(lastDate).format(AppConstants.ddMMYYYYHHmma_FORMAT);
  }

  public static checkNoOfDecimal(numberValue: number) {
    let hasDecimal = false;
    if (numberValue < 1) {
      const numberString = numberValue.toString();

      if (numberString.indexOf('.') > 0) {
        const deciamlString = numberString.substring(
          numberString.indexOf('.') + 1,
          numberString.length
        );
        if (deciamlString && deciamlString.length > 0) {
          hasDecimal = true;
        }
      }
    }
    return hasDecimal;
  }

  public static replaceUnderScore(string: string) {
    return (string && string.replace(/\_/g, ' ')) || '';
  }

  //TODO: This method needs to be converted to "TryParseJson"
  public static isValidJSON(string: string) {
    const obj = this.parseJSON(string);
    return obj ? true : false;
  }

  public static parseJSON(string: string) {
    try {
      return JSON.parse(string);
    } catch {
      return;
    }
  }

  public static getUniqueDataKey(key: dcAttributeDataKey) {
    return `${key.RootElementVersionID}-${key.DataComponentID}`;
  }

  public static getMonthQuarter(date: Date) {
    try {
      const month = moment(date).format('M');
      return Math.ceil(+month / 3);
    } catch (error) {
      console.log(error);
      return 0;
    }
  }

  public static findKeyByValueFromMap(
    map: Map<string | number, GenericValue>,
    value: GenericValue
  ) {
    let key;
    map.forEach((V, K) => {
      if (V === value) {
        key = K;
      }
    });
    return key;
  }

  public static getDataKeyCellName(colId: string) {
    return AppConstants.ReportConstants.DATAKEY_CELL_PREFIX + colId;
  }

  public static getCellValueIsGood(colId: string) {
    return AppConstants.ReportConstants.IS_GOOD + colId;
  }

  public static isTrueValue(value: string | boolean) {
    return /^true$|^1$/i.test(value as string);
  }

  public static getParentAttributePath(attributePath: string) {
    //Work around to get EEST child attribute
    const minChildPathLength = attributePath.includes('EEST_CALCULATIONS') ? 3 : 2;
    const splits = (attributePath && attributePath.split('|')) || [];
    if (splits.length > minChildPathLength) {
      // Work around to skip OE child attributes
      const lengthToSubstract = splits[splits.length - 1].includes('OE') ? 2 : 1;
      splits.length = splits.length - lengthToSubstract;
      attributePath = splits.join('|');
    }
    return attributePath;
  }

  public static formatElementPath(path: string, replaceChar = ' ', all = false) {
    const toReplace = all ? /\\/g : '\\';
    return path.replace('.', '').replace(toReplace, replaceChar);
  }

  public static isGoodValue(text: string) {
    return (
      text !== AppConstants.N_A &&
      text !== AppConstants.LOADING_STRING &&
      text !== AppConstants.NO_DATA_TEXT &&
      text !== AppConstants.Nodata_Text &&
      text !== AppConstants.ATTRIBUTE_DOES_TEXT &&
      text !== ''
    );
  }

  /**
   * Method to get agregate data based on configuration
   *
   * @param dataList array of number
   */
  public static getAggregatedData(dataList: number[], aggOption: PivotAggregates) {
    let data = 0;
    switch (aggOption) {
      case PivotAggregates.sum:
        data = addNumbers(dataList);
        break;
      case PivotAggregates.count:
        data = dataList.length;
        break;
      case PivotAggregates.first:
        data = dataList[0];
        break;
      case PivotAggregates.last:
        data = dataList[dataList.length - 1];
        break;
      case PivotAggregates.max:
        data = maxValue(dataList);
        break;
      case PivotAggregates.min:
        data = minValue(dataList);
        break;
      case PivotAggregates.avg:
      default:
        data = addNumbers(dataList) / dataList.length;
        break;
    }
    return data;
  }

  public static canTriggerForSingleElementApp(tags: dcTag[], appConfig: App) {
    if (appConfig.TriggerDefaultRequest) {
      return true;
    }

    if (!tags?.length) {
      return false;
    }

    let selectedTag = tags.find(tag => tag.isRemovable);
    if (!selectedTag) {
      selectedTag = tags[0];
    }

    if (
      appConfig &&
      ((appConfig.SupportedType === NFIdentity.Element &&
        selectedTag.SourceClassID === NFIdentity.Element) ||
        selectedTag.ElementTypeID === appConfig.SupportedType)
    ) {
      if (selectedTag.hasOwnProperty('expandable')) {
        return !(selectedTag as dcTagFlatTree).expandable;
      } else {
        return (
          (selectedTag.hasOwnProperty('isRemovable') && selectedTag.isRemovable) ||
          selectedTag.Level !== 0
        );
      }
    } else {
      return false;
    }
  }

  public static isValidDateColumn(colDef: ColDefExt, value: string | Date) {
    return (
      (colDef.hasOwnProperty('dataType') &&
        (colDef as ColDefExt).dataType === ValueType.DateTime) ||
      (!colDef.hasOwnProperty('dataType') && this.isValidDate(value))
    );
  }

  public static isValidDate(date: Date | string) {
    try {
      return moment(date).isValid();
    } catch (e) {
      console.log(e);
      return false;
    }
  }

  public static showFormulaSymbol(ConfigData: any, dataKey: dcAttributeDataKey) {
    if (ConfigData && ConfigData.Configuration) {
      let configObject = ConfigData.Configuration.filter(
        (config: any) => config.Id == dataKey.DataComponentID
      );
      ConfigData.Configuration.forEach((config: any) => {
        if (configObject && configObject.length == 0 && config.Configuration) {
          configObject = config.Configuration.filter(
            (comp: any) => comp.Id == dataKey.DataComponentID
          );
        }
      });
      if (configObject) return configObject;
      else return null;
    }
  }

  public static saveAsExcelFile(buffer: any, fileName: string): void {
    const data: Blob = new Blob([buffer], {
      type: AppConstants.EXCEL_TYPE
    });
    FileSaver.saveAs(data, fileName);
  }

  public static format1000Seperator(data: GenericValue | string) {
    const formattedNumber: string = data?.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return formattedNumber;
  }

  public static isNodata(data: dcValue) {
    if (data && data.Value) return data.Value.toString().toLowerCase() == AppConstants.NO_DATA_TEXT;
    else return false;
  }

  public static findDefaultRole(userDetails: User) {
    let currentUserRole: RoleAssignment;
    const assignments = userDetails.RoleAssignments.filter(
      roleAssignment => roleAssignment.GroupName == null
    );
    if (assignments && assignments.length > 0) {
      const selectedRoleId = +localStorage.getItem(AppConstants.SELECTED_ROLE_ID_KEY);
      if (selectedRoleId) {
        currentUserRole = assignments.find(a => a.Role.RoleID === selectedRoleId);
      }

      if (!currentUserRole) {
        const assignment = assignments.find(a => a.IsDefault);
        if (assignment) {
          currentUserRole = assignment;
        } else {
          currentUserRole = assignments[0];
        }
      }
    }
    return currentUserRole;
  }

  /**
   * Compares two Date objects and returns e number value that represents
   * the result:
   * 0 if the two dates are equal.
   * 1 if the first date is greater than second.
   * -1 if the first date is less than second.
   *
   * @param date1 First date object to compare.
   * @param date2 Second date object to compare.
   */
  public static compareDate(date1: Date, date2: Date): number {
    // With Date object we can compare dates them using the >, <, <= or >=.
    // The ==, !=, ===, and !== operators require to use date.getTime(),
    // so we need to create a new instance of Date with 'new Date()'
    const d1 = new Date(date1);
    const d2 = new Date(date2);

    // Check if the dates are equal
    const same = d1.getTime() === d2.getTime();
    if (same) return 0;

    // Check if the first is greater than second
    if (d1 > d2) return 1;

    // Check if the first is less than second
    if (d1 < d2) return -1;
  }

  public static UTCStringToNonUTC(date: string): string {
    return date.substring(0, 19).replace('T', ' ');
  }

  public static getComponentAccessRights(
    componentSecurityIDs: string,
    componentRoleIDs: string,
    loggedinUserRoles: UserRolePermission
  ): UserRolePermission {
    //Check if LoggedinUser's SecurityID is there in Configuration
    if (
      componentSecurityIDs &&
      componentSecurityIDs.indexOf(loggedinUserRoles.securityID.toString()) > -1
    ) {
      loggedinUserRoles.userRoles.forEach((role: RoleAccess) => {
        role.hasComponentAccess = true;
      });
      return loggedinUserRoles;
    }

    loggedinUserRoles.userRoles.forEach((role: RoleAccess) => {
      role.hasComponentAccess =
        componentRoleIDs && componentRoleIDs.indexOf(role.roleID.toString()) > -1 ? true : false;
    });
    return loggedinUserRoles;
  }

  public static getTimeZoneFromISOString(date: string) {
    return date.substring(19);
  }

  public static modifyTimeZone(date: string, timezone: string) {
    return date.substring(0, 19).concat(timezone);
  }

  public static removeTimeZoneRef(date: string) {
    return this.modifyTimeZone(date, '');
  }

  public static generateUniqueDarkColors(numColors: number, existingColors: string[] = []) {
    const colors = new Set<string>(existingColors);

    while (colors.size < numColors) {
      const color =
        '#' +
        this.generateRandomColor(2, 16) + // Red
        this.generateRandomColor(2, 16, 128) + // Green
        this.generateRandomColor(2, 16, 128); // Blue
      colors.add(color);
    }

    return Array.from(colors);
  }

  public static generateCachedUniqueColors(
    keys: string[],
    existingColors: string[] = []
  ): Map<string, string> {
    if (!keys?.length) {
      return;
    }

    const colors = new Set<string>(existingColors);
    const keyColors = new Map<string, string>();

    keys.forEach((key, index) => {
      const localStorageKey = `colorForKey:${key}`;
      let color: string | null = localStorage.getItem(localStorageKey);

      // If color does not exist in localStorage and there is no existing color at the current index, generate a new one
      if (!color) {
        color = existingColors[index] || this.generateRandomColor(6, 16, 16777215);
        // Add the new color to localStorage
        if (!localStorage.getItem(localStorageKey)) {
          localStorage.setItem(localStorageKey, color);
        }
      }

      colors.add(color);
      keyColors.set(key, color);
    });

    return keyColors;
  }

  private static generateRandomColor(
    padStartValue: number,
    toStringValue: number,
    mathResValue = 255
  ) {
    return (
      '#' +
      Math.floor(Math.random() * mathResValue)
        .toString(toStringValue)
        .padStart(padStartValue, '0')
    );
  }

  // This should be removed and should use dynamic function generateUniqueDarkColors
  public static getDistinctColor() {
    return [
      '#e6194b',
      '#3cb44b',
      '#ffe119',
      '#4363d8',
      '#f58231',
      '#911eb4',
      '#46f0f0',
      '#f032e6',
      '#bcf60c',
      '#fabebe',
      '#008080',
      '#e6beff',
      '#9a6324',
      '#fffac8',
      '#800000',
      '#aaffc3',
      '#808000',
      '#ffd8b1',
      '#000075',
      '#808080',
      '#ffffff',
      '#000000'
    ];
  }

  public static getDateFormattedValue(duration: moment.Duration, format: string): string {
    if (format) {
      const rgx = '[[^][]+]K|w+'; //skip the strings inside square bracket
      format = format
        .replace(rgx, '')
        .replace(/D+/g, 'D')
        .replace('D', Math.floor(duration.asDays()) + '');
      return moment.utc(duration.asMilliseconds()).format(format);
    }
    return this.getDaysHrsMinsSecs(duration);
  }

  public static getDaysHoursMinutes(duration: moment.Duration) {
    let ret = '';
    if (duration.days()) {
      ret = `${ret + duration.days()} Days `;
    }
    if (duration.hours()) {
      ret = `${ret + duration.hours()} Hours `;
    }
    if (duration.minutes()) {
      ret = `${ret + duration.minutes()} Minutes `;
    }
    return ret;
  }

  public static getDaysHrsMinsSecs(duration: moment.Duration) {
    let ret = this.getDaysHoursMinutes(duration);
    if (duration.seconds()) {
      ret = `${ret + duration.seconds()} seconds `;
    }
    return ret;
  }

  public static calculateImageResolution(
    srcWidth: number,
    srcHeight: number,
    maxHeight: number,
    maxWidth: number
  ): [width: number, height: number] {
    let resizeWidth: number = srcWidth;
    let resizeHeight: number = srcHeight;

    if (resizeWidth <= 0 || resizeHeight <= 0) {
      return [srcWidth, srcHeight];
    }
    let aspect: number = resizeWidth / resizeHeight;
    if (aspect > 0) {
      if (resizeWidth > maxWidth) {
        resizeWidth = maxWidth;
        resizeHeight = resizeWidth / aspect;
      }
      if (resizeHeight > maxHeight) {
        aspect = resizeWidth / resizeHeight;
        resizeHeight = maxHeight;
        resizeWidth = resizeHeight * aspect;
      }
    }
    if (resizeWidth <= 0 || resizeHeight <= 0) {
      return [srcWidth, srcHeight];
    }
    return [resizeWidth, resizeHeight];
  }

  public static getBooleanValue(value: any): boolean {
    if (typeof value === 'boolean') {
      return value;
    }
    if (typeof value === 'string') {
      const normalizedValue = value.trim().toLowerCase();
      if (normalizedValue === 'true') {
        return true;
      }
      if (normalizedValue === 'false') {
        return false;
      }
    }
    return false;
  }

  public static checkHasNoDefaultSelection(str: string | null | undefined): boolean {
    return !str || str.trim().length === 0;
  }

  /**
   * Sorts an array of objects by a specified date field using Moment.js.
   * - Supports string, number, Date, or Moment-compatible formats.
   * - Treats invalid or missing dates as the minimum possible date (1970-01-01).
   * - Normalizes date strings without time as UTC midnight.
   *
   * @param array - The array of objects to sort.
   * @param key - The key in each object that contains the date.
   * @param ascending - Whether to sort ascending (true) or descending (false).
   * @returns A sorted array.
   */
  public static sortByDate<T>(array: T[], key: keyof T, ascending: boolean = true) {
    array.sort((a, b) => {
      const dateA = this.normalizeDate(a[key]);
      const dateB = this.normalizeDate(b[key]);
      return ascending ? dateA.valueOf() - dateB.valueOf() : dateB.valueOf() - dateA.valueOf();
    });
  }

  /**
   * Normalizes a value into a Moment object.
   * - If the date string has no time, assumes UTC midnight.
   * - Invalid dates are replaced with Moment(0) (i.e., 1970-01-01T00:00:00Z).
   *
   * @param value - The value to normalize (string, number, Date).
   * @returns A valid Moment object.
   */
  public static normalizeDate(value: unknown): moment.Moment {
    if (value == null) return moment(0); // handles null, undefined

    let m: moment.Moment;

    if (typeof value === 'string') {
      const dateStr = value.trim();
      // Add UTC midnight if date only
      if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
        m = moment.utc(dateStr + 'T00:00:00Z');
      } else {
        m = moment(value);
      }
    } else {
      m = moment(value);
    }

    return m.isValid() ? m : moment(0);
  }

  /**
   * Formats numeric values or summary expressions like "sum(123456)" with thousand separators.
   *
   * - If the value is a number and formatting is enabled, it returns a formatted string (e.g., "1,234,567").
   * - If the value is a summary string (e.g., "sum(123456)", "avg(78.9)"), it extracts the number,
   *   formats it accordingly, and returns the reconstructed string (e.g., "sum(1,234,567)").
   * - For "avg", two decimal places are shown. Others (sum, min, max) show no decimals.
   * - If formatting is disabled or the value doesn't match expected patterns, the original value is returned.
   *
   * @param value - The raw value to format. Can be a number or a summary string.
   * @param use1000Seperator - Whether to format the value with a thousand separator.
   * @returns A formatted string representation of the value.
   */
  public static formatValueandSummary(value: any, use1000Seperator: boolean): string {
    if (use1000Seperator) {
      if (!value) return '';

      if (typeof value !== 'string') {
        return new Intl.NumberFormat('en-US').format(value);
      }

      // Handle summary string like "sum(123456)"
      const match = value.match(/(sum|avg|min|max)\(([^)]+)\)/i);
      if (!match) return value;

      const label = match[1]; // "sum"
      const rawNumber = match[2].replace(/,/g, '');
      const num = Number(rawNumber);

      if (isNaN(num)) return value;

      const formattedNum = new Intl.NumberFormat('en-US', {
        minimumFractionDigits: label === 'avg' ? 2 : 0,
        maximumFractionDigits: 2
      }).format(num);

      return `${label}(${formattedNum})`;
    }

    // Fallback: return raw value
    return value;
  }
}
